/**
 * useDraggable Hook
 *
 * Provides drag-and-drop functionality for floating panels with:
 * - Mouse and touch support
 * - Position persistence to localStorage
 * - Viewport boundary constraints
 * - Smooth drag experience
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface Position {
  x: number;
  y: number;
}

interface UseDraggableOptions {
  /** localStorage key for persisting position */
  storageKey: string;
  /** Default position if nothing stored */
  defaultPosition?: Position;
  /** Element dimensions for boundary calculations */
  elementSize?: { width: number; height: number };
  /** Padding from viewport edges */
  boundaryPadding?: number;
}

interface UseDraggableReturn {
  /** Current position */
  position: Position;
  /** Whether currently being dragged */
  isDragging: boolean;
  /** Props to spread on the draggable element */
  dragProps: {
    style: React.CSSProperties;
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
  /** Props to spread on the drag handle */
  handleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    style: React.CSSProperties;
  };
  /** Reset position to default */
  resetPosition: () => void;
}

/**
 * Hook for making elements draggable with position persistence
 */
export function useDraggable({
  storageKey,
  defaultPosition = { x: window.innerWidth - 416, y: window.innerHeight - 520 },
  elementSize = { width: 384, height: 500 },
  boundaryPadding = 16,
}: UseDraggableOptions): UseDraggableReturn {
  // Load initial position from localStorage or use default
  const getInitialPosition = useCallback((): Position => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as Position;
        // Validate stored position is within current viewport
        const maxX = window.innerWidth - elementSize.width - boundaryPadding;
        const maxY = window.innerHeight - elementSize.height - boundaryPadding;
        return {
          x: Math.max(boundaryPadding, Math.min(parsed.x, maxX)),
          y: Math.max(boundaryPadding, Math.min(parsed.y, maxY)),
        };
      }
    } catch {
      // Invalid stored data, use default
    }
    return defaultPosition;
  }, [storageKey, defaultPosition, elementSize, boundaryPadding]);

  const [position, setPosition] = useState<Position>(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);

  // Track drag offset (cursor position relative to element top-left)
  const dragOffset = useRef<Position>({ x: 0, y: 0 });

  // Track if drag was initiated from handle
  const dragFromHandle = useRef(false);

  // Constrain position within viewport
  const constrainPosition = useCallback(
    (x: number, y: number): Position => {
      const maxX = window.innerWidth - elementSize.width - boundaryPadding;
      const maxY = window.innerHeight - elementSize.height - boundaryPadding;
      return {
        x: Math.max(boundaryPadding, Math.min(x, maxX)),
        y: Math.max(boundaryPadding, Math.min(y, maxY)),
      };
    },
    [elementSize, boundaryPadding]
  );

  // Save position to localStorage
  const savePosition = useCallback(
    (pos: Position) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(pos));
      } catch {
        // localStorage might be full or disabled
      }
    },
    [storageKey]
  );

  // Handle mouse move during drag
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newPos = constrainPosition(
        e.clientX - dragOffset.current.x,
        e.clientY - dragOffset.current.y
      );
      setPosition(newPos);
    },
    [isDragging, constrainPosition]
  );

  // Handle touch move during drag
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;

      const touch = e.touches[0];
      const newPos = constrainPosition(
        touch.clientX - dragOffset.current.x,
        touch.clientY - dragOffset.current.y
      );
      setPosition(newPos);
    },
    [isDragging, constrainPosition]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      savePosition(position);
      dragFromHandle.current = false;
    }
  }, [isDragging, position, savePosition]);

  // Start drag from handle (for header bar)
  const startDragFromHandle = useCallback(
    (clientX: number, clientY: number) => {
      dragOffset.current = {
        x: clientX - position.x,
        y: clientY - position.y,
      };
      dragFromHandle.current = true;
      setIsDragging(true);
    },
    [position]
  );

  // Handle mouse down on drag handle
  const handleHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startDragFromHandle(e.clientX, e.clientY);
    },
    [startDragFromHandle]
  );

  // Handle touch start on drag handle
  const handleHandleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.stopPropagation();
      const touch = e.touches[0];
      startDragFromHandle(touch.clientX, touch.clientY);
    },
    [startDragFromHandle]
  );

  // Handle mouse down on element (only drag if on handle)
  const handleElementMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't initiate drag from element - only from handle
    // This allows interaction with buttons/content inside
  }, []);

  // Handle touch start on element
  const handleElementTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't initiate drag from element - only from handle
  }, []);

  // Reset position to default
  const resetPosition = useCallback(() => {
    const newPos = constrainPosition(defaultPosition.x, defaultPosition.y);
    setPosition(newPos);
    savePosition(newPos);
  }, [defaultPosition, constrainPosition, savePosition]);

  // Add/remove global event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      window.addEventListener('touchcancel', handleDragEnd);

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleDragEnd);
      window.removeEventListener('touchcancel', handleDragEnd);

      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, handleMouseMove, handleTouchMove, handleDragEnd]);

  // Handle window resize - revalidate position
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => constrainPosition(prev.x, prev.y));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [constrainPosition]);

  return {
    position,
    isDragging,
    dragProps: {
      style: {
        position: 'fixed' as const,
        left: position.x,
        top: position.y,
        // Remove bottom/right since we're using left/top
        bottom: 'auto',
        right: 'auto',
      },
      onMouseDown: handleElementMouseDown,
      onTouchStart: handleElementTouchStart,
    },
    handleProps: {
      onMouseDown: handleHandleMouseDown,
      onTouchStart: handleHandleTouchStart,
      style: {
        cursor: isDragging ? 'grabbing' : 'grab',
      },
    },
    resetPosition,
  };
}

export default useDraggable;
